import { api, endpoints } from "../api";
import { AxiosPromise } from "axios";
import { BaseApiPaging } from "api/types";

export enum CourseGroupAttributeType {
  Boolean = 1,
  Text = 2,
  Textarea = 3,
  DictionarySingleSelect = 4,
  DictionaryMultiSelect = 5,
  Wysiwyg = 6,
}

export const ATTRIBUTE_TYPE_LABELS: Record<number, string> = {
  [CourseGroupAttributeType.Boolean]: "Boolean",
  [CourseGroupAttributeType.Text]: "Tekst",
  [CourseGroupAttributeType.Textarea]: "Tekst wieloliniowy",
  [CourseGroupAttributeType.DictionarySingleSelect]: "Słownik (jednokrotny wybór)",
  [CourseGroupAttributeType.DictionaryMultiSelect]: "Słownik (wielokrotny wybór)",
  [CourseGroupAttributeType.Wysiwyg]: "WYSIWYG (HTML)",
};

export const ATTRIBUTE_TYPE_OPTIONS = Object.entries(ATTRIBUTE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export interface CourseGroupAttributeOptionApi {
  id: number;
  value: string;
  numericValue: number | null;
  sortOrder: number;
  color: string | null;
}

export interface CourseGroupAttributeApi {
  id: number;
  name: string;
  attributeType: CourseGroupAttributeType;
  options: CourseGroupAttributeOptionApi[];
}

export interface CourseGroupAttributeValueSelectionApi {
  id: number;
  optionId: number;
  optionValue: string;
  optionNumericValue: number | null;
  optionColor: string | null;
}

export interface CourseGroupAttributeValueApi {
  id: number;
  courseGroupAttributeId: number;
  attributeName: string;
  attributeType: CourseGroupAttributeType;
  boolValue: boolean | null;
  textValue: string | null;
  selections: CourseGroupAttributeValueSelectionApi[];
  availableOptions: CourseGroupAttributeOptionApi[];
}

export interface CreateCourseGroupAttributeRequest {
  name: string;
  attributeType: number;
  options?: { value: string; numericValue?: number | null; sortOrder: number; color?: string | null }[];
}

export interface UpdateCourseGroupAttributeRequest {
  name: string;
  attributeType: number;
  options?: { id?: number; value: string; numericValue?: number | null; sortOrder: number; color?: string | null }[];
}

export interface SaveCourseGroupAttributeValueRequest {
  courseGroupAttributeId: number;
  boolValue?: boolean | null;
  textValue?: string | null;
  selectedOptionIds?: number[];
}

export interface SaveCourseGroupAttributeValuesRequest {
  values: SaveCourseGroupAttributeValueRequest[];
}

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<CourseGroupAttributeApi>> => {
  return api.get<BaseApiPaging<CourseGroupAttributeApi>>(
    endpoints.COURSEGROUP_ATTRIBUTES.GET(skip, take, searchPhrase, orderBy),
  );
};

const fetchById = (id: number): AxiosPromise<CourseGroupAttributeApi> => {
  return api.get<CourseGroupAttributeApi>(endpoints.COURSEGROUP_ATTRIBUTES.GET_BY_ID(id));
};

const create = (data: CreateCourseGroupAttributeRequest): AxiosPromise<CourseGroupAttributeApi> => {
  return api.post<CourseGroupAttributeApi>(endpoints.COURSEGROUP_ATTRIBUTES.CREATE(), data);
};

const update = (id: number, data: UpdateCourseGroupAttributeRequest): AxiosPromise<void> => {
  return api.put(endpoints.COURSEGROUP_ATTRIBUTES.UPDATE(id), data);
};

const deleteAttribute = (id: number): Promise<void> => {
  return api.delete(endpoints.COURSEGROUP_ATTRIBUTES.DELETE(id));
};

const fetchValues = (courseGroupId: number): AxiosPromise<CourseGroupAttributeValueApi[]> => {
  return api.get<CourseGroupAttributeValueApi[]>(endpoints.COURSEGROUP_ATTRIBUTES.GET_VALUES(courseGroupId));
};

const saveValues = (courseGroupId: number, data: SaveCourseGroupAttributeValuesRequest): AxiosPromise<void> => {
  return api.put(endpoints.COURSEGROUP_ATTRIBUTES.SAVE_VALUES(courseGroupId), data);
};

export interface BulkAssignAttributeValueModel {
  courseGroupAttributeId: number;
  boolValue?: boolean | null;
  textValue?: string | null;
  selectedOptionIds?: number[];
}

export interface BulkAssignAttributesRequest {
  courseGroupIds: number[];
  courseGroupAttributeIds: number[];
  values?: BulkAssignAttributeValueModel[];
}

export interface BulkUnassignAttributesRequest {
  courseGroupIds: number[];
  courseGroupAttributeIds: number[];
}

const bulkAssign = (data: BulkAssignAttributesRequest): AxiosPromise<void> => {
  return api.post(endpoints.COURSEGROUP_ATTRIBUTES.BULK_ASSIGN(), data);
};

const bulkUnassign = (data: BulkUnassignAttributesRequest): AxiosPromise<void> => {
  return api.post(endpoints.COURSEGROUP_ATTRIBUTES.BULK_UNASSIGN(), data);
};

export const coursegroupAttributesApi = {
  fetch,
  fetchById,
  create,
  update,
  delete: deleteAttribute,
  fetchValues,
  saveValues,
  bulkAssign,
  bulkUnassign,
};
